import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Trash2, User, QrCode, Edit, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import ImageUpload from '../components/ImageUpload';

const Churrasqueiros = () => {
  const [churrasqueiros, setChurrasqueiros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingChurrasqueiro, setEditingChurrasqueiro] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    pix_key: '',
    pix_qrcode_url: '',
    active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await api.churrasqueiros.getAll();
      setChurrasqueiros(response.data);
    } catch (error) {
      toast.error('Erro ao carregar churrasqueiros');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingChurrasqueiro) {
        await api.churrasqueiros.update(editingChurrasqueiro.id, formData);
        toast.success('Churrasqueiro atualizado com sucesso!');
      } else {
        await api.churrasqueiros.create(formData);
        toast.success('Churrasqueiro cadastrado com sucesso!');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Erro ao salvar churrasqueiro');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este churrasqueiro?')) return;
    try {
      await api.churrasqueiros.delete(id);
      toast.success('Churrasqueiro excluído!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir churrasqueiro');
    }
  };

  const openEditDialog = (churrasqueiro) => {
    setEditingChurrasqueiro(churrasqueiro);
    setFormData({
      name: churrasqueiro.name,
      pix_key: churrasqueiro.pix_key || '',
      pix_qrcode_url: churrasqueiro.pix_qrcode_url || '',
      active: churrasqueiro.active
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingChurrasqueiro(null);
    setFormData({
      name: '',
      pix_key: '',
      pix_qrcode_url: '',
      active: true
    });
  };

  return (
    <div className="space-y-6" data-testid="churrasqueiros-page">
      <div className="flex items-center justify-between">
        <h1 className="text-5xl font-bold uppercase tracking-tight">Churrasqueiros</h1>
        <div className="flex gap-2">
          <Button 
            className="bg-secondary text-foreground hover:bg-secondary/80 font-bold uppercase border border-border"
            onClick={() => window.open('https://geradordepix.com/', '_blank')}
          >
            <ExternalLink className="mr-2 h-5 w-5" /> Novo QRCODE
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase" data-testid="add-churrasqueiro-button">
                <Plus className="mr-2 h-5 w-5" /> Novo Churrasqueiro
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-md" data-testid="churrasqueiro-dialog">
              <DialogHeader>
                <DialogTitle className="text-2xl uppercase font-bold">
                  {editingChurrasqueiro ? 'Editar Churrasqueiro' : 'Novo Churrasqueiro'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Nome</Label>
                  <Input
                    data-testid="churrasqueiro-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 font-mono"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Chave PIX</Label>
                  <Input
                    data-testid="churrasqueiro-pix-input"
                    value={formData.pix_key}
                    onChange={(e) => setFormData({ ...formData, pix_key: e.target.value })}
                    className="mt-1 font-mono"
                    placeholder="CPF, telefone ou email"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">QR Code PIX (Imagem)</Label>
                  <div className="mt-1">
                    <ImageUpload 
                      value={formData.pix_qrcode_url} 
                      onChange={(url) => setFormData({ ...formData, pix_qrcode_url: url })}
                      placeholder="Upload QR Code"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90 font-bold uppercase h-12" data-testid="churrasqueiro-submit-button">
                  {editingChurrasqueiro ? 'Atualizar' : 'Cadastrar'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-xl font-bold uppercase tracking-wide flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            Lista de Churrasqueiros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground font-mono uppercase text-xs">Nome</TableHead>
                <TableHead className="text-muted-foreground font-mono uppercase text-xs">Chave PIX</TableHead>
                <TableHead className="text-muted-foreground font-mono uppercase text-xs">QR Code</TableHead>
                <TableHead className="text-muted-foreground font-mono uppercase text-xs text-right">Ação</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {churrasqueiros.map((c) => (
                <TableRow key={c.id} className="border-border/50 hover:bg-secondary/50">
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="font-mono text-sm">{c.pix_key}</TableCell>
                  <TableCell>
                    {c.pix_qrcode_url ? (
                      <div className="relative group">
                        <QrCode className="h-5 w-5 text-primary cursor-pointer" />
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block p-2 bg-white rounded shadow-lg z-10 border border-border w-64 h-64 flex items-center justify-center">
                          <img 
                            src={c.pix_qrcode_url} 
                            alt="QR Code" 
                            className="w-full h-full object-contain" 
                          />
                        </div>
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-xs">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(c)}
                        className="hover:bg-primary/10 hover:text-primary"
                        data-testid={`edit-churrasqueiro-${c.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(c.id)}
                        className="hover:bg-destructive/10 hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {churrasqueiros.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    Nenhum churrasqueiro cadastrado.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Churrasqueiros;
